import React, { useState, useEffect } from 'react';
import { tasksAPI, boardsAPI, User, Board, Column } from '../api/api';
import { useAuth } from '../context/AuthContext';
import UserSelector from './UserSelector';
import './Modal.css';

interface CreateTaskModalProps {
  onClose: () => void;
  onTaskCreated: () => void;
  boardId?: number;
  columnId?: number;
  defaultAssignedUserId?: number;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  onClose,
  onTaskCreated,
  boardId,
  columnId,
  defaultAssignedUserId,
}) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState<number | undefined>(defaultAssignedUserId);
  const [assignedToIds, setAssignedToIds] = useState<number[]>(defaultAssignedUserId ? [defaultAssignedUserId] : []);
  const [reviewersIds, setReviewersIds] = useState<number[]>([]);
  const [deadline, setDeadline] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<number | undefined>(boardId);
  const [selectedColumnId, setSelectedColumnId] = useState<number | undefined>(columnId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
    // Если доска не передана извне (не из Kanban-доски), загружаем список досок
    if (!boardId) {
      loadBoards();
    }
  }, [boardId]);

  const loadUsers = async () => {
    try {
      const data = await tasksAPI.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
    }
  };

  const loadBoards = async () => {
    try {
      const data = await boardsAPI.getAll();
      setBoards(data);
    } catch (error) {
      console.error('Ошибка при загрузке досок:', error);
    }
  };

  const handleBoardChange = async (value: string) => {
    const id = value ? parseInt(value) : undefined;
    setSelectedBoardId(id);
    setSelectedColumnId(undefined);

    if (!id) {
      setColumns([]);
      return;
    }

    try {
      const board = await boardsAPI.getById(id);
      setColumns(board.columns || []);
    } catch (error) {
      console.error('Ошибка при загрузке столбцов доски:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Название задачи обязательно');
      return;
    }

    const effectiveBoardId = boardId ?? selectedBoardId;
    const effectiveColumnId = columnId ?? selectedColumnId;

    if (!effectiveBoardId || !effectiveColumnId) {
      setError('Доска и столбец обязательны');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await tasksAPI.create(
        title,
        description || undefined,
        assignedToIds[0], // для обратной совместимости
        assignedToIds.length > 0 ? assignedToIds : undefined,
        reviewersIds.length > 0 ? reviewersIds : undefined,
        effectiveBoardId,
        effectiveColumnId,
        undefined,
        deadline || undefined
      );
      onTaskCreated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при создании задачи');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Создать задачу</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>
          <UserSelector
            label="Исполнители"
            users={users}
            selectedIds={assignedToIds}
            onChange={(ids) => {
              setAssignedToIds(ids);
              setAssignedTo(ids[0]); // для обратной совместимости
            }}
            placeholder="Выберите исполнителей"
          />
          <UserSelector
            label="Проверяющие"
            users={users}
            selectedIds={reviewersIds}
            onChange={setReviewersIds}
            placeholder="Выберите проверяющих"
            hint="Если не выбраны, проверять может только создатель задачи"
            excludeUserIds={user?.id ? [user.id] : []}
          />
          <div className="form-group">
            <label>Дедлайн</label>
            <input
              type="datetime-local"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
          {/* Если модалка открыта не из конкретной доски/столбца, даем выбрать проект и этап */}
          {!boardId && (
            <>
              <div className="form-group">
                <label>Доска</label>
                <select
                  value={selectedBoardId || ''}
                  onChange={(e) => handleBoardChange(e.target.value)}
                >
                  <option value="">Не выбрана</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Столбец</label>
                <select
                  value={selectedColumnId || ''}
                  onChange={(e) =>
                    setSelectedColumnId(e.target.value ? parseInt(e.target.value) : undefined)
                  }
                  disabled={!selectedBoardId}
                >
                  <option value="">Не выбран</option>
                  {columns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {error && <div className="error">{error}</div>}
          <div className="modal-actions">
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Создание...' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTaskModal;

